// ═══════════════════════════════════════════════════════════════════════════════
// Server feature flags (e.g. global translation toggle from TRANSLATION_ENABLED)
// ═══════════════════════════════════════════════════════════════════════════════

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { healthApi } from '../lib/api';
import { useAuth } from './AuthContext';

type FeaturesState = {
  translationEnabled: boolean;
  isLoading: boolean;
};

type FeaturesContextValue = FeaturesState;

const FeaturesContext = createContext<FeaturesContextValue | null>(null);

export const useFeatures = (): FeaturesContextValue => {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
};

/** Whether translation UI (icon + translated text) should be shown for the current user. */
export const useTranslationUiEnabled = (): boolean => {
  const { translationEnabled } = useFeatures();
  const { user } = useAuth();
  if (!translationEnabled) {
    return false;
  }
  return user?.settings?.autoTranslationEnabled !== false;
};

type HealthFeaturesPayload = {
  features?: { translationEnabled?: boolean };
};

const readTranslationEnabledFromHealth = (res: unknown): boolean => {
  if (!res || typeof res !== 'object') {
    return false;
  }
  const payload = res as HealthFeaturesPayload & {
    success?: boolean;
    data?: HealthFeaturesPayload;
  };
  if (payload.data?.features?.translationEnabled === true) {
    return true;
  }
  return payload.features?.translationEnabled === true;
};

type FeaturesProviderProps = {
  children: ReactNode;
};

export const FeaturesProvider = ({ children }: FeaturesProviderProps) => {
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const load = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const res = await healthApi.get();
        setTranslationEnabled(readTranslationEnabledFromHealth(res));
      } catch {
        setTranslationEnabled(false);
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, []);

  const value = useMemo(
    (): FeaturesContextValue => ({
      translationEnabled,
      isLoading,
    }),
    [translationEnabled, isLoading],
  );

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
};
